<script setup lang="ts">
import ButtonOrders from '@/assets/icons/Buttons/ButtonOrders.vue';
import ButtonProfile from '@/assets/icons/Buttons/ButtonProfile.vue';
import HotIcon from '@/assets/icons/MenuFilters/HotIcon.vue';
import DiningIcon from '@/assets/icons/Menus/DiningIcon.vue';
import NavTimeClockIcon from '@/assets/icons/NavBar/NavTimeClockIcon.vue';
import CreditCardIcon from '@/assets/icons/Profile/CreditCardIcon.vue';
import ImagePlaceholderIcon from '@/assets/icons/Profile/ImagePlaceholderIcon.vue';
import OnboardingIcon from '@/assets/icons/Profile/OnboardingIcon.vue';
import TeamIcon from '@/assets/icons/Profile/TeamIcon.vue';
import ProfileMenuItem from '@/components/Profile/ProfileMenuItem.vue';
import { useFetch } from '@/composables/useFetch';
import { useImageBase64 } from '@/composables/useImageBase64';
import type { MessageSchema } from '@/i18n/index.js';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import type { AttributesList, Restaurant } from '@/types/restaurants';
import { hasSubscriptionFeature } from '@/utils/auth';
import { useToast } from 'primevue/usetoast';
import { computed, markRaw, ref, watchEffect, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const toast = useToast();
const router = useRouter();

const user = ref(useUserStore().user);
const { fetchData } = useFetch();
const fileInput: Ref<HTMLInputElement | null> = ref(null);
const restaurant: Ref<Restaurant | null> = ref(null);
const attributes: Ref<AttributesList | null> = ref(null);
const isUploadingLogo = ref(false);
const userStore = useUserStore();

const logoUrl = computed(() => restaurant.value?.logo_url ?? null);
const { imageSrc: logoImageSrc, refresh: refreshLogo } = useImageBase64(logoUrl);
// const envStore = useEnvStore();

watchEffect(async () => {
  if (user.value?.restaurants[0].id && !restaurant.value) {
    const { data: dbRestaurant, error: dbRestaurantError } = await fetchData(
      useEnvStore().apiUrl + '/restaurants/' + user.value?.restaurants[0].id,
      'GET',
    );
    if (dbRestaurantError) console.log(dbRestaurantError);
    else restaurant.value = dbRestaurant;

    const { data: dbAttributes, error: dbAttributesError } = await fetchData(useEnvStore().apiUrl + '/restaurants/attributes', 'GET');
    if (dbAttributesError) console.log(dbAttributesError);
    else attributes.value = dbAttributes;
  }
});

const userInfos = computed(() => {
  if (user.value) {
    return {
      fullName: `${user.value.first_name} ${user.value.last_name}`,
      restaurantName: user.value.restaurants[0].name,
      restaurationType: user.value.restaurants[0].restauration_type,
    };
  }
  return {
    fullName: '',
    restaurantName: '',
    restaurationType: '',
  };
});

async function uploadRestaurantLogo(event: Event) {
  const target = event.target as HTMLInputElement | null;
  if (!target || !target.files) return;
  const files = target.files;

  if (files.length === 0) {
    console.warn('No files selected');
    return;
  }

  if (!restaurant.value?.id) {
    console.error('Restaurant ID not found');
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('errors.restaurantNotFound') || 'Restaurant not found',
      life: 3000,
    });
    return;
  }

  const file: File = files[0];
  const maxFileSize = 10 * 1024 * 1024;

  // Validate file size
  if (file.size > maxFileSize) {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('menuDishView.fileTooLarge', { maxSizeMB: '10' }),
      life: 3000,
    });
    if (target) target.value = '';
    return;
  }

  // Validate file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'];
  if (!validTypes.includes(file.type)) {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('errors.invalidFileType') || 'Invalid file type. Please upload a JPEG, PNG, or WebP image.',
      life: 3000,
    });
    if (target) target.value = '';
    return;
  }

  isUploadingLogo.value = true;
  const formData = new FormData();
  formData.append('logo', file);

  try {
    console.log('[uploadRestaurantLogo] Starting upload for restaurant:', restaurant.value.id);
    const { data, error, statusCode } = await fetchData(`${useEnvStore().apiUrl}/restaurants/${restaurant.value.id}/logo`, 'POST', formData);

    if (error) {
      console.error('[uploadRestaurantLogo] Upload failed:', error);

      // Extract the most specific error message
      let errorMessage = t('menuDishView.addUserImageError');

      if (error?.details?.errors?.logo?.[0]) {
        errorMessage = error.details.errors.logo[0];
      } else if (error?.details?.message) {
        errorMessage = error.details.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: errorMessage,
        life: 5000,
      });
    } else if (data && restaurant.value) {
      console.log('[uploadRestaurantLogo] Upload successful');

      // Update the restaurant logo
      restaurant.value.logo_url = data.logo_url;

      // Force re-fetch of the logo image (URL may not have changed)
      refreshLogo();

      // Refresh user data from server
      await userStore.refreshUser();

      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('menuDishView.addUserImageSuccess') || 'Logo updated successfully',
        life: 3000,
      });
    } else {
      // No data and no error - unexpected response
      console.warn('[uploadRestaurantLogo] Unexpected response:', { data, error, statusCode });
      toast.add({
        severity: 'warn',
        summary: t('common.warning') || 'Warning',
        detail: t('errors.unexpectedResponse') || 'Unexpected response from server',
        life: 3000,
      });
    }
  } catch (error) {
    console.error('[uploadRestaurantLogo] Unexpected error:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('menuDishView.addUserImageError'),
      life: 5000,
    });
  } finally {
    isUploadingLogo.value = false;
    // Reset file input to allow re-uploading the same file
    if (target) target.value = '';
  }
}

const allLinks = [
  {
    name: t('profile.profile.profileTab.profileTabLabel'),
    icon: markRaw(ButtonProfile),
    url: '/profile/my-profile',
    class: 'onboarding-user-profil-link',
  },
  {
    name: t('profile.profile.restaurantTab.restaurantTabLabel'),
    icon: markRaw(DiningIcon),
    url: '/profile/my-restaurant',
    class: 'onboarding-restaurant-profil-link',
  },
  {
    name: t('profile.profile.restaurantTab.other.mySuppliers'),
    icon: markRaw(HotIcon),
    url: '/profile/suppliers',
    feature: { key: 'module_orders', min: true },
  },
  {
    name: t('profile.profile.restaurantTab.other.orders'),
    icon: markRaw(ButtonOrders),
    url: '/profile/orders',
  },
  // "Team" link will be conditionally included below
  {
    name: t('profile.profile.restaurantTab.team'),
    icon: markRaw(TeamIcon),
    url: '/profile/team',
    class: 'onboarding-team-profil-link',
    feature: { key: 'module_staff', min: true },
  },
  {
    name: t('profile.profile.restaurantTab.other.subscriptions'),
    icon: markRaw(CreditCardIcon),
    url: '/profile/subscriptions',
  },
  {
    name: t('navBar.timeclock'),
    icon: markRaw(NavTimeClockIcon),
    url: '/timeclock',
    feature: { key: 'module_staff', min: true },
  },
  {
    name: t('profile.onboarding.statusTitle'),
    icon: markRaw(OnboardingIcon),
    url: '/onboarding',
    class: 'onboarding-status-profil-link',
    feature: { key: 'employees_included', min: 1 },
  },
];

// Filter links based on features
const links = computed(() => allLinks.filter((link) => !link.feature || hasSubscriptionFeature(user.value, link.feature.key, link.feature.min)));

// const nonProductionEnvironments = ['local', 'development', 'staging']

// if (nonProductionEnvironments.includes(envStore.environment)) {
//   links.push({
//     name: t('profile.profile.restaurantTab.team'),
//     icon: markRaw(ButtonProfile),
//     url: '/profile/team',
//   })
// }
</script>

<template>
  <div>
    <!-- ProfileCard -->
    <div class="mb-6 flex flex-row items-center justify-items-start gap-4">
      <div class="flex justify-center">
        <!-- Le div contenant le cercle -->
        <div
          class="relative flex aspect-square h-24 max-h-24 w-24 max-w-24 cursor-pointer items-center justify-center rounded-full border-2 border-primary-200 object-cover p-1"
          :class="{ 'pointer-events-none opacity-50': isUploadingLogo }"
          @click="fileInput?.click()"
        >
          <!-- Loading spinner -->
          <div v-if="isUploadingLogo" class="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-30">
            <i class="pi pi-spin pi-spinner text-2xl text-white"></i>
          </div>
          <!-- Affiche l'image si disponible -->
          <img
v-if="logoImageSrc" :src="logoImageSrc"
            alt="Profile picture"
            class="absolute inset-0 h-full w-full rounded-full object-cover p-1"
          />

          <!-- Affiche l'icône de placeholder si pas d'image -->
          <ImagePlaceholderIcon v-else class="h-8 w-8 text-primary-950" />
        </div>

        <!-- Input pour télécharger une image -->
        <input
          id="dish-image"
          ref="fileInput"
          type="file"
          name="dish-image"
          accept="image/jpeg, image/jpg, image/png, image/webp, image.heic"
          class="mb-4 hidden"
          @change="uploadRestaurantLogo"
        />
      </div>
      <div class="">
        <h1 class="mb-2">{{ userInfos.restaurantName }}</h1>
        <p class="font-titles">{{ userInfos.fullName }}</p>
        <p class="font-titles text-sm">{{ t(`restaurationType.${userInfos.restaurationType}`) }}</p>
      </div>
    </div>

    <!-- Navigation Links -->
    <div class="flex-grow">
      <ProfileMenuItem
        v-for="(link, index) in links"
        :key="link.name"
        :name="link.name"
        :icon="link.icon"
        :url="link.url"
        :index="index"
        :class-name="link.class"
        @click="router.push({ path: link.url })"
      />
    </div>
  </div>
</template>
